import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck, FiZap, FiShield, FiUsers, FiStar, FiActivity, FiArrowRight, FiLock, FiTrendingUp } = FiIcons;

const AccordionStep = ({ number, title, desc, isOpen, onClick }) => (
  <div 
    onClick={onClick}
    className={`cursor-pointer border transition-all duration-300 rounded-2xl p-5 mb-3 ${isOpen ? 'bg-indigo-900/20 border-indigo-500' : 'bg-[#0B0E14] border-gray-800 hover:border-gray-600'}`}
  >
    <div className="flex items-center gap-4">
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${isOpen ? 'bg-indigo-500 text-white' : 'bg-gray-800 text-gray-400'}`}>
        {number}
      </div>
      <div className="flex-1 text-left">
        <h3 className={`font-bold text-lg leading-tight ${isOpen ? 'text-white' : 'text-gray-300'}`}>{title}</h3>
      </div>
      <SafeIcon icon={FiIcons.FiChevronDown} className={`text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
    </div>
    {isOpen && (
      <motion.div 
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        className="text-left pl-12 pt-3"
      >
        <p className="text-indigo-200 text-sm leading-relaxed">{desc}</p>
      </motion.div>
    )}
  </div>
);

const LandingOptimized = () => {
  const [activeStep, setActiveStep] = useState(1);

  return (
    <div className="min-h-screen bg-[#050505] text-white font-sans selection:bg-indigo-500 selection:text-white">
      
      {/* Sticky Discount Banner */}
      <div className="bg-indigo-600 text-white text-xs font-bold py-2 text-center px-4">
        ⚡ FLASH SALE: Get 50% OFF the Annual Recovery Plan today only!
      </div>

      <div className="max-w-md mx-auto px-4 py-8 flex flex-col items-center">
        
        {/* Social Proof Header */}
        <div className="flex items-center gap-2 mb-6 opacity-80">
          <div className="flex -space-x-2">
            <img src="https://i.pravatar.cc/100?img=11" className="w-6 h-6 rounded-full border border-black" alt="user" />
            <img src="https://i.pravatar.cc/100?img=12" className="w-6 h-6 rounded-full border border-black" alt="user" />
            <img src="https://i.pravatar.cc/100?img=13" className="w-6 h-6 rounded-full border border-black" alt="user" />
          </div>
          <p className="text-xs text-gray-400">Join <span className="text-white font-bold">50,000+</span> members</p>
        </div>

        {/* Hero Headline */}
        <motion.h1 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-5xl font-extrabold text-center mb-2 leading-tight bg-gradient-to-r from-white via-gray-200 to-gray-500 bg-clip-text text-transparent"
        >
          Break Free.
        </motion.h1>
        <p className="text-gray-400 text-center mb-8 text-lg">Your addiction ends today.</p>

        {/* Interactive Steps */}
        <div className="w-full mb-8">
          <div className="flex items-center justify-between mb-4">
             <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">Your Journey</span>
             <span className="text-xs text-indigo-400 bg-indigo-900/30 px-2 py-1 rounded">3 min setup</span>
          </div>
          
          <AccordionStep 
            number="1"
            title="Commit to Change"
            desc="The hardest part is deciding. By clicking below, you're taking the first psychological step towards freedom."
            isOpen={activeStep === 1}
            onClick={() => setActiveStep(1)}
          />
          <AccordionStep 
            number="2"
            title="Get Your Custom Plan"
            desc="Download the QuitApp and answer 5 questions to generate your personalized neuro-science based recovery path."
            isOpen={activeStep === 2}
            onClick={() => setActiveStep(2)}
          />
          <AccordionStep 
            number="3"
            title="Join the Squad"
            desc="Get instantly matched with an anonymous accountability partner who is on the exact same timeline as you."
            isOpen={activeStep === 3}
            onClick={() => setActiveStep(3)}
          />
        </div>

        {/* Value Proposition Box */}
        <div className="w-full bg-gradient-to-br from-indigo-900/40 to-purple-900/40 border border-indigo-500/30 rounded-2xl p-6 mb-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-indigo-500 blur-[50px] opacity-30"></div>
          <div className="flex items-start gap-3 relative z-10">
            <div className="p-2 bg-indigo-500 rounded-lg shrink-0">
               <SafeIcon icon={FiTrendingUp} className="text-white text-xl" />
            </div>
            <div>
              <h4 className="font-bold text-white mb-1">Why this works</h4>
              <p className="text-indigo-200 text-sm leading-relaxed">
                Willpower fails. Systems don't. We use CBT (Cognitive Behavioral Therapy) triggers to rewire your brain's reward system.
              </p>
            </div>
          </div>
        </div>

        {/* Main CTA */}
        <motion.button 
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full bg-white text-black rounded-xl py-4 font-extrabold text-xl shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_30px_rgba(255,255,255,0.5)] transition-all flex items-center justify-center gap-2 mb-4 group"
        >
          <span>Start Recovery Now</span>
          <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
        </motion.button>
        
        <p className="text-xs text-gray-500 text-center mb-10 flex items-center justify-center gap-1">
          <SafeIcon icon={FiLock} className="text-green-500" /> No credit card required for free assessment
        </p>

        {/* Enhanced Trust Grid */}
        <div className="grid grid-cols-2 gap-4 w-full opacity-80">
          <div className="flex flex-col items-center p-4 bg-[#0B0E14] rounded-xl border border-gray-800 text-center">
             <div className="text-yellow-400 flex gap-0.5 mb-2">
               {[1,2,3,4,5].map(i => <SafeIcon key={i} icon={FiStar} className="fill-current text-xs" />)}
             </div>
             <span className="text-xs font-bold text-gray-300">4.9/5 Average Rating</span>
          </div>
          <div className="flex flex-col items-center p-4 bg-[#0B0E14] rounded-xl border border-gray-800 text-center">
             <div className="text-green-500 mb-2">
               <SafeIcon icon={FiShield} className="text-lg" />
             </div>
             <span className="text-xs font-bold text-gray-300">HIPAA Compliant</span>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-12 flex gap-6 text-xs text-gray-600">
          <a href="#" className="hover:text-gray-400">Privacy Policy</a>
          <a href="#" className="hover:text-gray-400">Terms of Service</a>
          <a href="#" className="hover:text-gray-400">Restore Purchase</a>
        </div>

      </div>
    </div>
  );
};

export default LandingOptimized;